import LuxonUtils from '@date-io/luxon';
// eslint-disable-next-line no-restricted-imports
import { Box, Button, ClickAwayListener, Grid, Grow, Popper, withStyles } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { DateTime } from 'luxon';
import React from 'react';
import { useBoolean } from 'react-use-boolean';
import { toLocalDate } from '../formatters';
import { Paper } from '../Paper';
import { DateRangeFilterProps } from './DateRangeFilter';
import { FilterCalendar } from './FilterCalendar';

const CustomButton = withStyles((theme) => ({
  root: {
    textTransform: 'none',
    border: 'solid 1px #c4c4c4',
    margin: theme.spacing(0, 1),
    minWidth: 120,
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
}))(Button);

export type DateRangeValue = string | null;

export interface DateRangePickerProps extends Omit<DateRangeFilterProps, 'label' | 'columnId'> {
  selectedStartDate: DateRangeValue;
  selectedEndDate: DateRangeValue;
  onSelectStartDate: (date: DateRangeValue) => void;
  onSelectEndDate: (date: DateRangeValue) => void;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  selectedStartDate,
  selectedEndDate,
  labelStart,
  labelEnd,
  onSelectStartDate,
  onSelectEndDate,
}) => {
  const anchorRefStart = React.useRef<HTMLButtonElement>(null);
  const anchorRefEnd = React.useRef<HTMLButtonElement>(null);
  const [openStart, startAction] = useBoolean();
  const [openEnd, endAction] = useBoolean();
  const startDate = selectedStartDate ? DateTime.fromISO(selectedStartDate) : DateTime.now();
  const endDate = selectedEndDate ? DateTime.fromISO(selectedEndDate) : startDate;

  return (
    <Grid container direction='column'>
      <Grid>
        <Box textAlign='center'>
          <CustomButton
            variant={'text'}
            ref={anchorRefStart}
            onClick={() => {
              startAction.on();
              endAction.off();
            }}
            disableRipple
          >
            {selectedStartDate ? toLocalDate(startDate) : labelStart}
          </CustomButton>
          -
          <CustomButton
            variant={'text'}
            ref={anchorRefEnd}
            disableRipple
            onClick={() => {
              if (selectedStartDate) {
                startAction.off();
                endAction.on();
              }
            }}
          >
            {selectedEndDate ? toLocalDate(endDate) : labelEnd}
          </CustomButton>
        </Box>
      </Grid>
      <MuiPickersUtilsProvider utils={LuxonUtils}>
        <Popper
          open={openStart}
          anchorEl={anchorRefStart.current}
          transition
          disablePortal
          role={'picker'}
          placement='bottom-start'
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper>
                <ClickAwayListener onClickAway={startAction.off}>
                  <Box overflow='hidden'>
                    <FilterCalendar
                      selectedDate={selectedStartDate}
                      date={startDate}
                      onChange={(date) => {
                        onSelectStartDate(date.toISODate());
                        if (selectedEndDate && date.valueOf() > Date.parse(selectedEndDate)) {
                          onSelectEndDate(null);
                        }
                        startAction.off();
                        endAction.on();
                      }}
                    />
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        <Popper
          open={openEnd}
          anchorEl={anchorRefEnd.current}
          transition
          disablePortal
          role={'picker'}
          placement='bottom-start'
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper>
                <ClickAwayListener onClickAway={endAction.off}>
                  <Box overflow='hidden'>
                    <FilterCalendar
                      selectedDate={selectedEndDate}
                      minDate={startDate}
                      date={endDate}
                      onChange={(date) => {
                        endAction.off();
                        onSelectEndDate(date.toISODate());
                      }}
                    />
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </MuiPickersUtilsProvider>
    </Grid>
  );
};
